import { COUNTRIES } from '@/lib/countries/countryCodes';
import { countryLabel } from '@/lib/countries/nationalityOptions';
import type { OrderStatement, OrderStatementRecipient } from '../types';
import { formatDateTime } from '../format';
import {
  formatMoney,
  normalizeDocumentLocale,
  paymentMethodLabel,
  printLabelsFor,
} from '../printLabels';
import { DocumentHeader } from './DocumentHeader';
import { OrderFormFieldValuesTable } from './OrderFormFieldValuesTable';

interface OrderStatementDocumentProps {
  doc: OrderStatement;
}

/**
 * [233] ISO alpha-2 국가코드 → 주문 locale 표시명. 미등록 코드만 코드 그대로 폴백
 * (countryCode 는 ISO 표준 공개 코드라 내부 식별자 아님; 표시명 우선, 빈값보단 코드).
 */
const resolveCountryName = (
  code: string | null | undefined,
  locale: string,
): string | null => {
  if (!code) return null;
  const match = COUNTRIES.find((c) => c.code.toUpperCase() === code.toUpperCase());
  return match ? countryLabel(match, locale) : code;
};

/**
 * 수령인 주소를 한 줄로 합친다. 없으면 '-'. 우편번호·주소·도시·주/도까지 포함.
 * 국가는 국가/국적과 무관하게 별도 행으로 분리 노출하므로(KR 포함, 생략 금지) 여기엔 넣지 않는다.
 */
const formatRecipientAddress = (r: OrderStatementRecipient | null): string => {
  if (!r) return '-';
  const parts = [r.postalCode, r.address1, r.address2, r.city, r.stateProvince].filter(
    (s): s is string => Boolean(s),
  );
  return parts.length > 0 ? parts.join(' ') : '-';
};

/**
 * 주문내역서(§5).
 * 코드=상품코드 / 공급사명·상품이미지·결제정보&수단 표시.
 * 관리자메모는 응답에 없음(표시안함). 하단 추가정보도 미표시.
 *
 * [글로벌몰] 라벨·금액·결제수단·국가명은 **주문 발생 몰 locale**(doc.localeCode) 기준 —
 * 관리자 UI 언어가 아니라 주문의 몰 기준(일본몰=일본어/JPY). 한국몰은 종전과 동일(회귀 0).
 */
export const OrderStatementDocument = ({ doc }: OrderStatementDocumentProps) => {
  const locale = normalizeDocumentLocale(doc.localeCode);
  const labels = printLabelsFor(locale);
  const money = (m: Parameters<typeof formatMoney>[0]) => formatMoney(m, locale);

  const paymentLabel = paymentMethodLabel(locale, doc.payment.method);

  // QA-517 해외몰(외화 결제) 주문 — 품목 금액·결제 요약 옆에 원화(KRW) 금액을 같이 적는다. BE 가 원화 금액을 줄 때만.
  const foreign = (doc.currencyCode ?? doc.payment.payableAmount.currencyCode) !== 'KRW';
  const showKrwLines = foreign && doc.items.some((i) => i.krwItemTotal);
  const krw = foreign ? (doc.payment.krw ?? null) : null;
  const krwMoney = (m: Parameters<typeof formatMoney>[0]) => formatMoney(m, 'ko-KR');

  // [233] 필드 존재(field-presence) 렌더 — 국가/몰 분기 없이 값이 있는 필드는 모두 노출한다.
  // 한국(KR) 주문도 영문수령인·국가 등 값이 있으면 전부 표시(country-conditional 제거).
  const recipient = doc.recipient;
  const hasEnglishName = Boolean(recipient?.englishName);
  const hasMobile = Boolean(recipient?.mobile);
  const hasRemarks = Boolean(recipient?.remarks);
  // 국가는 국적과 무관하게 값(countryCode)이 있으면 표시(KR → "대한민국", 생략하지 않음).
  const countryName = resolveCountryName(recipient?.countryCode, locale);

  return (
    <article className="print-surface">
      <DocumentHeader
        title={labels.kind['order-statement']}
        header={{
          shopName: labels.shopName,
          orderNo: doc.orderNo,
          issuedAt: doc.orderedAt,
        }}
        localeCode={locale}
      />

      {/* 주문자/수령 정보 */}
      <table style={{ marginBottom: 12 }}>
        <tbody>
          <tr>
            <th style={{ width: '20%' }}>{labels.field.ordererName}</th>
            <td>{doc.ordererName ?? '-'}</td>
            <th style={{ width: '20%' }}>{labels.field.ordererMobile}</th>
            <td>{doc.ordererPhone ?? '-'}</td>
          </tr>
          <tr>
            <th>{labels.field.recipient}</th>
            <td>{doc.recipient?.name ?? '-'}</td>
            <th>{labels.field.recipientPhone}</th>
            <td>{doc.recipient?.phone ?? '-'}</td>
          </tr>
          {/* [233] 영문 수령인·휴대폰 — 국가 무관, 값이 있으면 노출(field-presence). */}
          {hasEnglishName || hasMobile ? (
            <tr>
              <th>{labels.field.recipientEnglishName}</th>
              <td>{recipient?.englishName ?? '-'}</td>
              <th>{labels.field.recipientMobile}</th>
              <td>{recipient?.mobile ?? '-'}</td>
            </tr>
          ) : null}
          {/* [233] 국가 — countryCode 값이 있으면 국적/몰 무관하게 노출(KR=대한민국 포함). */}
          {countryName ? (
            <tr>
              <th>{labels.field.country}</th>
              <td colSpan={3}>{countryName}</td>
            </tr>
          ) : null}
          <tr>
            <th>{labels.field.shippingAddress}</th>
            <td colSpan={3}>{formatRecipientAddress(doc.recipient)}</td>
          </tr>
          {hasRemarks ? (
            <tr>
              <th>{labels.field.remarks}</th>
              <td colSpan={3} style={{ whiteSpace: 'pre-line' }}>
                {recipient?.remarks}
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>

      <table>
        <thead>
          <tr>
            <th>{labels.col.image}</th>
            <th>{labels.col.code}</th>
            <th>{labels.col.productName}</th>
            <th>{labels.col.vendor}</th>
            <th>{labels.col.option}</th>
            <th className="pd-num">{labels.col.qty}</th>
            <th className="pd-num">{labels.col.unitPrice}</th>
            <th className="pd-num">{labels.col.lineAmount}</th>
            {showKrwLines ? <th className="pd-num">{labels.col.lineAmountKrw}</th> : null}
          </tr>
        </thead>
        <tbody>
          {doc.items.map((item, idx) => (
            <tr key={`${item.productCode}-${idx}`}>
              <td>
                {item.imageUrl ? (
                  <img className="pd-img" src={item.imageUrl} alt="" />
                ) : (
                  '-'
                )}
              </td>
              {/* 항목4 — 코드=자체상품코드(be-order 가 UUID→실 productCode 로 정정).
                  legacy 주문은 productCode 가 빈 문자열로 오므로(UUID 비노출) '-' 로 표기. */}
              <td>{item.productCode || '-'}</td>
              <td>{item.productName}</td>
              <td>{item.supplierName ?? '-'}</td>
              <td>{item.optionLabel ?? '-'}</td>
              <td className="pd-num">{item.quantity}</td>
              <td className="pd-num">{money(item.unitPrice)}</td>
              <td className="pd-num">{money(item.itemTotal)}</td>
              {showKrwLines ? (
                <td className="pd-num" data-testid="statement-line-krw">
                  {item.krwItemTotal ? krwMoney(item.krwItemTotal) : '-'}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>

      {/* 결제정보 & 수단(§5) — 마일리지·예치금 포함 전체 결제 내역. */}
      <table style={{ marginTop: 12, width: krw ? '75%' : '60%', marginLeft: 'auto' }} data-testid="statement-payment">
        <tbody>
          {krw ? (
            // QA-517 — 결제통화 금액 옆에 같은 항목의 원화 금액.
            <tr>
              <th />
              <th className="pd-num">{doc.currencyCode ?? doc.payment.payableAmount.currencyCode}</th>
              <th className="pd-num">{labels.payment.krw}</th>
            </tr>
          ) : null}
          <tr>
            <th>{labels.payment.method}</th>
            <td colSpan={krw ? 2 : 1}>{paymentLabel}</td>
          </tr>
          {doc.payment.paidAt ? (
            <tr>
              <th>{labels.payment.paidAt}</th>
              <td colSpan={krw ? 2 : 1}>{formatDateTime(doc.payment.paidAt)}</td>
            </tr>
          ) : null}
          <tr>
            <th>{labels.summary.subtotal}</th>
            <td className="pd-num">{money(doc.payment.itemsSubtotal)}</td>
            {krw ? <td className="pd-num">{krwMoney(krw.itemsSubtotal)}</td> : null}
          </tr>
          <tr>
            <th>{labels.summary.discount}</th>
            <td className="pd-num">- {money(doc.payment.discountAmount)}</td>
            {krw ? <td className="pd-num">- {krwMoney(krw.discountAmount)}</td> : null}
          </tr>
          <tr>
            <th>{labels.summary.shippingFee}</th>
            <td className="pd-num">{money(doc.payment.shippingFee)}</td>
            {krw ? <td className="pd-num">{krwMoney(krw.shippingFee)}</td> : null}
          </tr>
          <tr>
            <th>{labels.payment.mileageUsed}</th>
            <td className="pd-num">- {money(doc.payment.mileageUsed)}</td>
            {krw ? <td className="pd-num">- {krwMoney(krw.mileageUsed)}</td> : null}
          </tr>
          <tr>
            <th>{labels.payment.depositUsed}</th>
            <td className="pd-num">- {money(doc.payment.depositUsed)}</td>
            {krw ? <td className="pd-num">- {krwMoney(krw.depositUsed)}</td> : null}
          </tr>
          <tr>
            <th style={{ fontSize: 14 }}>{labels.payment.payable}</th>
            <td style={{ fontSize: 14, fontWeight: 700 }} className="pd-num">
              {money(doc.payment.payableAmount)}
            </td>
            {krw ? (
              <td style={{ fontSize: 14, fontWeight: 700 }} className="pd-num" data-testid="statement-payable-krw">
                {krwMoney(krw.payableAmount)}
              </td>
            ) : null}
          </tr>
        </tbody>
      </table>

      {/* [233] 주문서 추가정보(통관 등) — 값이 있을 때만 표시. 배송 필수 정보라 문서에 동봉. */}
      <OrderFormFieldValuesTable values={doc.orderFormFieldValues} locale={locale} />
    </article>
  );
};
